import React, { useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';
import { partitionByTermination, dedupTermedByPlan } from './ClientTerminationHelper';

// Sample raw data (replace with your actual dataset)
const rawData = [/* your JSON data here */];

// Flatten and enrich data
const prepareData = (data) => {
  return data.flatMap(group => 
    group.rates.map(rate => { 
      let newMonthlyPremium = rate.newTotalPremium * (rate.eeCount)
      let currentMonthlyPremium = rate.currentTotalPremium * (rate.eeCount)
      let newAnnualPremium = (rate.newTotalPremium * (rate.eeCount)) * 12 // assuming monthly premium
      let currentAnnualPremium = (rate.currentTotalPremium * (rate.eeCount)) * 12
      return ({
      id: `${group.groupName}-${rate.clientRateModelId}`,
      groupName: group.groupName,
      planId: rate.planId,
      planName: rate.planName,
      planType: rate.planOfferType,
      planTypeDescription: rate.planTypeDescription,
      currentTotalPremium: rate.currentTotalPremium,
      newTotalPremium: rate.newTotalPremium,
      eeCount: rate.eeCount,
      newMonthlyPremium,
      currentMonthlyPremium,
      newAnnualPremium,
      currentAnnualPremium,
      isTermed: rate.isTermed === true,
      terminationType: rate.terminationType || null,
      isCarrierTerminated: rate.isCarrierTerminated === true,
      mappedRenewalPlanName: rate.mappedRenewalPlanName || null,
      percentageChange:
        (currentMonthlyPremium === 0
          ? newMonthlyPremium === 0 ? 0 : 100
          : (newMonthlyPremium - currentMonthlyPremium) / currentMonthlyPremium * 100)
    })})
  );
  
};
function summarizeByPlanType(data) {
  const result = {};

  data.forEach(item => {
    const plan = item.planType;

    if (!result[plan]) {
      result[plan] = {
        planType: plan === 'MED'  ? 'Medical' : plan === 'DEN' ? 'Dental' : 'Vision',
        currentTotalPremium: 0,
        newTotalPremium: 0,
        eeCount: 0,
        newMonthlyPremium: 0,
        currentMonthlyPremium: 0,
        newAnnualPremium: 0,
        currentAnnualPremium: 0
      };
    }

    result[plan].currentTotalPremium += item.currentTotalPremium || 0;
    result[plan].newTotalPremium += item.newTotalPremium || 0;
    result[plan].eeCount += item.eeCount || 0;
    result[plan].newMonthlyPremium += item.newMonthlyPremium || 0;
    result[plan].currentMonthlyPremium += item.currentMonthlyPremium || 0;
    result[plan].newAnnualPremium += item.newAnnualPremium || 0;
    result[plan].currentAnnualPremium += item.currentAnnualPremium || 0;
  });

  // Add percentageChange to each group
  for (const plan in result) {
    const group = result[plan];
    const { currentAnnualPremium, newAnnualPremium } = group;

    group.percentageChange =
      currentAnnualPremium !== 0
        ? ((newAnnualPremium - currentAnnualPremium) / currentAnnualPremium) * 100
        : 0;
  }

  return Object.values(result);
}

const TermedPlansSection = ({ carrierTermed, clientTermed }) => {
  // Same plan can appear once per benefit group upstream; collapse to one row per plan for this rollup view.
  const carrierRows = dedupTermedByPlan(carrierTermed);
  const clientRows = dedupTermedByPlan(clientTermed);
  if (!carrierRows.length && !clientRows.length) return null;
  const planTypeLabel = (type) => type === 'MED' ? 'Medical' : type === 'DEN' ? 'Dental' : 'Vision';

  return (
    <div style={{ marginTop: 16 }}>
      {carrierRows.length > 0 && (
        <>
          <div style={{ fontSize: 13, color: '#666', fontStyle: 'italic', marginBottom: 6 }}>
            The following plans are no longer available from the carrier and have been excluded from totals and rate calculations.
          </div>
          <Grid data={carrierRows} dataItemKey="id" style={{ height: 'auto' }}>
            <GridColumn
              field="planName"
              title="Plan"
              cell={(props) => (
                <td style={{ color: '#999' }}>
                  {props.dataItem.planName}
                  <span style={{
                    marginLeft: 8,
                    fontSize: 11,
                    padding: '1px 6px',
                    borderRadius: 10,
                    backgroundColor: '#e0e0e0',
                    color: '#666',
                  }}>
                    No Longer Available
                  </span>
                </td>
              )}
            />
            <GridColumn
              field="planType"
              title="Type"
              cell={(props) => <td style={{ color: '#999' }}>{planTypeLabel(props.dataItem.planType)}</td>}
            />
            <GridColumn
              field="currentMonthlyPremium"
              title="Last Known Monthly Premium"
              cell={(props) => (
                <td style={{ color: '#999', textAlign: 'right' }}>
                  {props.dataItem.currentMonthlyPremium > 0
                    ? `$${props.dataItem.currentMonthlyPremium.toFixed(2)}`
                    : '--'}
                </td>
              )}
            />
          </Grid>
        </>
      )}
      {clientRows.length > 0 && (
        <>
          <div style={{ fontSize: 13, color: '#666', fontStyle: 'italic', marginBottom: 6, marginTop: carrierRows.length > 0 ? 12 : 0 }}>
            The following plans are being replaced. They are excluded from totals and rate calculations.
          </div>
          <Grid data={clientRows} dataItemKey="id" style={{ height: 'auto' }}>
            <GridColumn field="planName" title="Current Plan" />
            <GridColumn
              field="planType"
              title="Type"
              cell={(props) => <td>{planTypeLabel(props.dataItem.planType)}</td>}
            />
            <GridColumn
              field="currentMonthlyPremium"
              title="Current Monthly Premium"
              format="{0:c}"
              className="k-text-right"
            />
            <GridColumn
              field="mappedRenewalPlanName"
              title="Renewal Plan"
              cell={(props) => (
                <td style={{ fontStyle: props.dataItem.mappedRenewalPlanName ? 'normal' : 'italic', color: props.dataItem.mappedRenewalPlanName ? 'inherit' : '#999' }}>
                  {props.dataItem.mappedRenewalPlanName || 'Pending assignment'}
                </td>
              )}
            />
          </Grid>
        </>
      )}
    </div>
  );
};

const RateGrid = ({data}) => {
  
  const flatData = data
  const [expanded, setExpanded] = useState({});
  const [groupExpand, setGroupExpand] = React.useState([]);
    const handleGroupExpandChange = React.useCallback((event) => {
        setGroupExpand(event.groupExpand);
    }, []);
  // Expand toggle handler
  const onExpandChange = (event) => {
    const groupValue = event.dataItem.group.groupName;
    setExpanded(prev => ({
      ...prev,
      [groupValue]: !prev[groupValue]
    }));
  };

  const expandField = 'expanded'; // used by Kendo Grid for expandable rows
const initialGroup = [
    {
        field: 'groupName'
    }
];
  // Mark each data item with its current expanded state
  const dataWithExpansion = flatData.map(item => ({
    ...item,
    [expandField]: expanded[item.groupName] ?? true
  }));

  return (
    <Grid
      data={dataWithExpansion}
      style={{ height: 'auto' }}
    >
      <GridColumn field="planType" title="Plan Type" />
      <GridColumn field="eeCount" className="k-text-center" title="# Enrolled" format="{0}"  />
      <GridColumn field="currentMonthlyPremium" className="k-text-right" title="Current Monthly Premium" format="{0:c}"  />
      <GridColumn field="newMonthlyPremium" className="k-text-right" title="New Monthly Premium" format="{0:c}" />
      <GridColumn 
        field="percentageChange"
        className="k-text-center"
        title="% Change"
        cell={(props) => {
          const value = props.dataItem[props.field];
          const formatted = value.toFixed(2) + '%';
          const color = value > 0 ? 'green' : value < 0 ? 'red' : 'gray';
          return <td style={{ color, textAlign: 'center' }}>{formatted}</td>;
        }}
      />
    </Grid>
  );
};

export default function ClientRates(newModelDetailData) {
  let gridData = newModelDetailData && newModelDetailData.data && newModelDetailData.data.groups ? prepareData(newModelDetailData.data.groups) : []
  const hasTermedPlans = newModelDetailData?.data?.termedPlans?.length > 0;

  if (gridData) {
    const { active, carrierTermed, clientTermed } = partitionByTermination(gridData);
    let summarizedData = summarizeByPlanType(active);
    return (
      <>
        {hasTermedPlans ? (
          <div style={{ fontStyle: 'italic', color: '#666', marginBottom: 8 }}>
            Plan-type rollups are not available because termed plans are included for comparison context.
          </div>
        ) : (
          <RateGrid data={summarizedData} />
        )}
        <TermedPlansSection carrierTermed={carrierTermed} clientTermed={clientTermed} />
      </>
    );
  }
}